// Where a game is reviewed: `/review/{website}/{username}/{date}/{id}`. The
// id is what the screen looks the game up by; the website, username and
// date are there so the address says what it opens when it is shared. The
// date is informational and never parsed.
import type { GameSource, GameSummary, Phase } from '@chess/api-model';

/** The react-router pattern the review screen registers. */
export const REVIEW_ROUTE = '/review/:website/:username/:date/:id';

/** The route's parameters, as react-router hands them out. */
export interface ReviewRouteParams {
  website: string;
  username: string;
  date: string;
  id: string;
}

/** The website segment for a game's source: `chess.com` becomes `chesscom`. */
export function websiteOf(source: GameSource): string {
  return source.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

/** The address a game is reviewed at. */
export function reviewPath(game: Pick<GameSummary, 'id' | 'owner' | 'source' | 'playedAt'>): string {
  const date = game.playedAt.slice(0, 10) || 'undated';
  return `/review/${websiteOf(game.source)}/${encodeURIComponent(game.owner)}/${date}/${encodeURIComponent(game.id)}`;
}

/**
 * The stage a screen is focused on, carried in the address as `?stage=`
 * (2026-10-13): a stage screen's links into the library carry it, the
 * library keeps it and passes it to each game's review, and the review
 * opens on that stage's tab and first position. Home's own stage filter
 * uses the same parameter and values. Purely navigation: never a filter.
 */
export const STAGE_PARAM = 'stage';
const PHASES: readonly Phase[] = ['opening', 'middlegame', 'ending'];

/** The stage an address asks for, or undefined when it names none or an unknown one. */
export function stageOf(search: string): Phase | undefined {
  const raw = new URLSearchParams(search).get(STAGE_PARAM);
  return (PHASES as readonly string[]).includes(raw ?? '') ? (raw as Phase) : undefined;
}

/** The path or query with `stage` set (or left out when undefined); an existing `stage` is replaced. */
export function withStage(pathOrQuery: string, stage: Phase | undefined): string {
  const hashAt = pathOrQuery.indexOf('#');
  const hash = hashAt >= 0 ? pathOrQuery.slice(hashAt) : '';
  const base = hashAt >= 0 ? pathOrQuery.slice(0, hashAt) : pathOrQuery;
  const q = base.indexOf('?');
  const path = q >= 0 ? base.slice(0, q) : base;
  const params = new URLSearchParams(q >= 0 ? base.slice(q) : '');
  if (stage) params.set(STAGE_PARAM, stage);
  else params.delete(STAGE_PARAM);
  const query = params.toString().replace(/%2C/g, ',');
  return `${path}${query ? `?${query}` : ''}${hash}`;
}
